import { customElement, property } from "lit/decorators.js";
import { css, html, LitElement, TemplateResult, CSSResultGroup } from "lit";
import { Annotation, compareAnnotationOrders, isUserAnnotation } from "@/state/Annotations";
import { MachineAnnotation } from "@/state/MachineAnnotations";
import { StateController } from "@dodona/lit-state";

/**
 * A marker that styles the slotted content based on the relevant annotations.
 * It applies a background color to user annotations and a wavy underline to machine annotations.
 *
 * @prop {Annotation[]} annotations - The annotations to use for styling.
 * @prop {boolean} fullWidth - Whether the marker should be full width
 *
 * @element d-annotation-marker
 */
@customElement("d-annotation-marker")
export class AnnotationMarker extends LitElement {
    @property({ type: Array })
    annotations: Annotation[] = [];

    @property({ type: Boolean, attribute: "full-width" })
    fullWidth = false;

    state = new StateController(this);

    static get styles(): CSSResultGroup {
        // order matters here, the last defined class determines the color if multiple apply
        return css`
            .type-info,
            .type-warning,
            .type-error,
            .type-info-intense,
            .type-warning-intense,
            .type-error-intense {
                background-position: left bottom;
                background-repeat: repeat-x;
            }
            .type-info {
                background-image: var(--d-annotation-info-background);
            }
            .type-warning {
                background-image: var(--d-annotation-warning-background);
            }
            .type-error {
                background-image: var(--d-annotation-error-background);
            }
            .type-info-intense {
                background-image: var(--d-annotation-info-background-intense);
            }
            .type-warning-intense {
                background-image: var(--d-annotation-warning-background-intense);
            }
            .type-error-intense {
                background-image: var(--d-annotation-error-background-intense);
            }
            .type-annotation {
                background-color: var(--annotation-color);
            }
            .type-question {
                background-color: var(--question-color);
            }
            .type-annotation-intense {
                background-color: var(--annotation-intense-color);
            }
            .type-question-intense {
                background-color: var(--question-intense-color);
            }
        `;
    }

    static getClass(annotation: Annotation): string {
        return annotation.isHovered ? `type-${annotation.type}-intense` : `type-${annotation.type}`;
    }

    static colors = {
        error: "var(--d-annotation-error, red)",
        warning: "var(--d-annotation-warning, yellow)",
        info: "var(--d-annotation-info, blue)",
        annotation: "var(--annotation-color, green)",
        question: "var(--question-color, orange)",
        "annotation-intense": "var(--annotation-intense-color, green)",
        "question-intense": "var(--question-intense-color, orange)",
    };

    /**
     * Returns the annotations sorted in order of importance.
     * Hovered annotations are prioritized over non-hovered annotations.
     * Otherwise the default order is used, defined in `compareAnnotationOrders`.
     *
     * Goal is to always show the style of the most important annotation.
     */
    get sortedAnnotations(): Annotation[] {
        // IMPORTANT: Array.sort is in-place; don't mutate `this.annotations`
        return [...this.annotations].sort((a, b) => {
            if (a.isHovered && !b.isHovered) return -1;
            if (b.isHovered && !a.isHovered) return 1;
            return compareAnnotationOrders(a, b);
        });
    }

    get machineAnnotationMarkerSVG(): TemplateResult | undefined {
        const firstMachineAnnotation = this.sortedAnnotations.find((a) => !isUserAnnotation(a)) as
            MachineAnnotation | undefined;

        if (!firstMachineAnnotation) return undefined;

        const size = firstMachineAnnotation.isHovered ? 20 : 14;
        return html`<svg
            style="position: absolute; top: ${16 - size / 2}px; left: -${size / 2}px"
            width="${size}"
            height="${size}"
            viewBox="0 0 24 24"
            aria-hidden="true"
            focusable="false"
        >
            <path
                fill="${AnnotationMarker.colors[firstMachineAnnotation.type]}"
                d="M7.41 15.41L12 10.83l4.59 4.58L18 14l-6-6l-6 6l1.41 1.41Z"
            />
        </svg>`;
    }

    get annotationClasses(): string {
        return this.annotations.map((a) => AnnotationMarker.getClass(a)).join(" ");
    }

    render(): TemplateResult {
        // Watch out, additional whitespace in the template string affects layout!
        const classes = `${this.annotationClasses} ${this.fullWidth ? "full-width" : ""}`;
        return html`<span class="${classes}"><slot>${this.machineAnnotationMarkerSVG}</slot></span>`;
    }
}
